import { useState, useCallback, useRef } from "react";
import { uploadAsset, deleteAsset } from "../../services/assetsApi";
import { config } from "../../config/environment";
import { IMAGE_MIME_TYPES, getAcceptString, hasValidExtension } from "../../utils/fileTypes";
import {
  Box,
  Button,
  Icon,
  Image,
  Spinner,
  Text,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { MdUpload, MdDelete, MdImage } from "react-icons/md";
import { useParams } from "react-router-dom";
import { useRoomStore } from "../../store";
import { flushEditorSave } from "../../pages/EditorPage";

const MAX_FILE_SIZE = config.upload.maxFileSize.wallImage;
const MAX_FILE_SIZE_MB = Math.round(MAX_FILE_SIZE / (1024 * 1024));

export default function ImageUploadForWalls() {
  const { roomId } = useParams();
  const fileInputRef = useRef(null);

  const materialOptions = useRoomStore((s) => s.materialOptions);
  const setMaterialOptions = useRoomStore((s) => s.setMaterialOptions);
  const setMaterialType = useRoomStore((s) => s.setMaterialType);
  const setMaterialId = useRoomStore((s) => s.setMaterialId);

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const currentImageUrl = materialOptions?.src || null;
  const currentAssetKey = materialOptions?.assetKey || null;

  const handleFileSelect = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type (using canonical types from fileTypes.js — no GIF)
      const hasValidMime = IMAGE_MIME_TYPES.includes(file.type);
      const extOk = hasValidExtension(file.name, 'image');

      if (!hasValidMime && !extOk) {
        setError("Only PNG, JPEG, and WebP images are supported");
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setError(`File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
        return;
      }

      setUploading(true);
      setError(null);

      try {
        const result = await uploadAsset(file, { roomId });

        if (result.success) {
          const imageUrl = result.data.publicUrl || result.data.url;
          setMaterialOptions({
            ...materialOptions,
            src: imageUrl,
            assetKey: result.data.assetKey,
          });
          setMaterialType("image");
          setMaterialId("21");
          setTimeout(() => flushEditorSave(), 0);
        } else {
          setError(result.error?.message || "Upload failed");
        }
      } catch (err) {
        setError(err.message || "Upload failed");
      } finally {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [roomId, materialOptions, setMaterialOptions, setMaterialType, setMaterialId]
  );

  const handleDelete = useCallback(async () => {
    const clearImage = () => {
      setMaterialOptions({
        ...materialOptions,
        src: null,
        assetKey: null,
      });
      setMaterialType("basic");
      setMaterialId("0");
    };

    if (!currentAssetKey) {
      clearImage();
      return;
    }

    try {
      await deleteAsset(currentAssetKey, { roomId });
      clearImage();
    } catch (err) {
      console.error("Failed to delete image asset:", err);
      clearImage();
    }
  }, [roomId, currentAssetKey, materialOptions, setMaterialOptions, setMaterialType, setMaterialId]);

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box mt={4}>
      <Text fontSize="sm" mb={3} color="gray.300" fontWeight="medium">
        Upload Image:
      </Text>

      <input
        ref={fileInputRef}
        type="file"
        accept={getAcceptString('image')}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {currentImageUrl ? (
        <VStack spacing={3} align="stretch">
          {/* Image preview */}
          <Box
            position="relative"
            bg="gray.900"
            borderRadius="0"
            overflow="hidden"
            border="2px solid"
            borderColor="gray.600"
          >
            <Image
              src={currentImageUrl}
              alt="Wall image"
              width="100%"
              maxHeight="150px"
              objectFit="contain"
            />
            <Box
              position="absolute"
              top={2}
              right={2}
              bg="blackAlpha.700"
              borderRadius="0"
              p={1}
            >
              <Icon as={MdImage} color="white" boxSize={4} />
            </Box>
          </Box>

          {/* Actions */}
          <HStack spacing={2}>
            <Button
              size="sm"
              onClick={triggerFileSelect}
              isLoading={uploading}
              loadingText="Uploading..."
              flex={1}
              bg="gray.700"
              _hover={{ bg: "gray.600" }}
              borderRadius="0"
            >
              <Icon as={MdUpload} mr={1} />
              Replace
            </Button>
            <Button
              size="sm"
              onClick={handleDelete}
              colorScheme="red"
              variant="outline"
              flex={1}
              borderRadius="0"
            >
              <Icon as={MdDelete} mr={1} />
              Remove
            </Button>
          </HStack>
        </VStack>
      ) : (
        <VStack spacing={3} align="stretch">
          {/* Upload button */}
          <Button
            onClick={triggerFileSelect}
            isLoading={uploading}
            loadingText="Uploading..."
            size="md"
            height="80px"
            bg="gray.800"
            border="2px dashed"
            borderColor="gray.600"
            _hover={{ bg: "gray.700", borderColor: "gray.500" }}
            borderRadius="0"
          >
            <VStack spacing={1}>
              {uploading ? (
                <Spinner size="md" />
              ) : (
                <>
                  <Icon as={MdUpload} boxSize={6} />
                  <Text fontSize="sm">Upload Image</Text>
                </>
              )}
            </VStack>
          </Button>

          {/* Help text */}
          <Text fontSize="xs" color="gray.500" textAlign="center">
            PNG, JPEG, or WebP up to {MAX_FILE_SIZE_MB}MB
          </Text>
        </VStack>
      )}

      {/* Error message */}
      {error && (
        <Text fontSize="xs" color="red.400" mt={2} textAlign="center">
          {error}
        </Text>
      )}
    </Box>
  );
}
